import { useEffect, useRef, useState } from "react";

import { AlertTriangle, XCircle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTaskStatus } from "@/hooks/queries/use-bundle-splitter";
import { useIsSessionActive } from "@/hooks/useIsSessionActive";

import { FilePreview } from "../FilePreview";
import { STUCK_THRESHOLD_MS, computePollInterval } from "./pollingSchedule";

type PollingPhaseProps = {
  file: File;
  taskId: string;
  onProcessingComplete: () => void;
  onDismiss?: () => void;
};

const TERMINAL_STATES = new Set(["SUCCESS", "FAILURE", "REVOKED"]);

export const PollingPhase = ({
  file,
  taskId,
  onProcessingComplete,
  onDismiss,
}: PollingPhaseProps) => {
  const [processingStep, setProcessingStep] = useState<string>("Processing");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isTerminal, setIsTerminal] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  // useState lazy-initializer keeps render pure while pinning the mount time.
  const [mountedAt] = useState(() => Date.now());
  const lastProgressValueRef = useRef<number | null>(null);
  const lastProgressAtRef = useRef<number | null>(null);

  // MDRS-267: the bundle toast lives outside ProtectedRoute, so its polling
  // would otherwise keep firing (and painting live progress) after the session
  // expires. Gate both the query and its interval on session validity so it
  // stops the instant the user is no longer authenticated.
  const isSessionActive = useIsSessionActive();

  const { data: taskStatus, isSuccess: isStatusSuccess } = useTaskStatus({
    task_id: taskId,
    enabled: !!taskId && isSessionActive,
    // Stepped backoff: cadence lengthens as the task runs longer, keeping the
    // first 30 s responsive while preventing long-session poll storms. An
    // expired session halts polling entirely.
    refetchInterval:
      isTerminal || !isSessionActive
        ? false
        : () => computePollInterval(Date.now() - mountedAt),
  });

  const isFailed =
    taskStatus?.state === "FAILURE" || taskStatus?.state === "REVOKED";
  const isRetrying = taskStatus?.state === "RETRY";

  useEffect(() => {
    if (!isStatusSuccess || !taskStatus) return;

    if (taskStatus.progress?.step) {
      setProcessingStep(taskStatus.progress.step.replace(/\s+with\s+\w+/gi, ""));
    }

    if (taskStatus.progress?.progress != null) {
      const current = taskStatus.progress.progress;
      setProcessingProgress(current);

      // Watchdog: time-based so it's robust to changing poll cadence. We
      // record the timestamp of the last advancement and trip if no progress
      // has been observed for STUCK_THRESHOLD_MS. RETRY responses report
      // progress=null and are skipped (transient, not a real stall).
      if (
        lastProgressValueRef.current === null ||
        current > lastProgressValueRef.current
      ) {
        lastProgressValueRef.current = current;
        lastProgressAtRef.current = Date.now();
      } else if (
        current === lastProgressValueRef.current &&
        lastProgressAtRef.current !== null &&
        Date.now() - lastProgressAtRef.current >= STUCK_THRESHOLD_MS
      ) {
        setIsStuck(true);
        setIsTerminal(true);
      }
    }

    if (TERMINAL_STATES.has(taskStatus.state)) {
      setIsTerminal(true);
    }

    if (taskStatus.state === "SUCCESS") {
      onProcessingComplete();
    }
  }, [taskStatus, isStatusSuccess, onProcessingComplete]);

  return (
    <>
      <FilePreview
        file={file}
        uploadProgress={isFailed || isStuck ? undefined : processingProgress}
        isFailed={isFailed}
        onClear={isFailed || isStuck ? onDismiss : undefined}
      />

      {isFailed ? (
        <Alert variant="destructive" className="mt-2">
          <XCircle className="size-4" />
          <AlertDescription>
            Processing failed. Please dismiss this file and try again.
          </AlertDescription>
        </Alert>
      ) : isStuck ? (
        <Alert className="mt-2">
          <AlertTriangle className="size-4" />
          <AlertDescription>
            Taking longer than expected. Please dismiss and try again.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <p className="text-center text-xs text-foreground-muted">
            {isRetrying ? "Retrying…" : processingStep}
          </p>
          <p className="mt-2 text-center text-xs text-foreground-muted">
            This may take a few minutes depending on number and size of documents.
          </p>
        </>
      )}
    </>
  );
};
